import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import type { CreateInboundForwarding, InboundForwarding, InboundMessage, UpdateInboundForwarding } from 'types/inbound'
import type { MutationResult } from 'utils/api'
import { qs } from 'utils/api'

import { cacheRetriedInboundMessage, cacheSavedInboundForwarding } from '../../lib/inbound-cache'
import { useApiQuery } from '../useApiQuery'
import { useFetchApi } from '../useFetchApi'

export function useInboundForwardings() {
  return useApiQuery<InboundForwarding[]>('/api/inbound-forwardings')
}

export function useInboundForwarding(id: string | undefined) {
  return useApiQuery<InboundForwarding>(id ? `/api/inbound-forwardings/${encodeURIComponent(id)}` : null)
}

export function useInboundMessages(params?: { query?: string; forwardingId?: string; limit?: number }) {
  return useApiQuery<InboundMessage[]>(`/api/inbound-messages${qs({ limit: 50, ...params })}`, {
    refetchInterval: 15_000,
  })
}

export function useInboundMessage(id: string | null) {
  return useApiQuery<InboundMessage>(id ? `/api/inbound-messages/${encodeURIComponent(id)}` : null)
}

export function useCreateInboundForwarding(): MutationResult<InboundForwarding, [CreateInboundForwarding]> {
  const { fetchApi } = useFetchApi()
  const client = useQueryClient()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (payload: CreateInboundForwarding) => {
      setLoading(true)
      try {
        const forwarding = await fetchApi<InboundForwarding>('/api/inbound-forwardings', {
          method: 'POST',
          body: JSON.stringify(payload),
        })
        await cacheSavedInboundForwarding(client, forwarding)
        return forwarding
      } finally {
        setLoading(false)
      }
    },
    [client, fetchApi],
  )
  return { perform, loading }
}

export function useUpdateInboundForwarding(): MutationResult<InboundForwarding, [string, UpdateInboundForwarding]> {
  const { fetchApi } = useFetchApi()
  const client = useQueryClient()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (id: string, payload: UpdateInboundForwarding) => {
      setLoading(true)
      try {
        const url = `/api/inbound-forwardings/${encodeURIComponent(id)}`
        const forwarding = await fetchApi<InboundForwarding>(url, {
          method: 'PATCH',
          body: JSON.stringify(payload),
        })
        await cacheSavedInboundForwarding(client, forwarding)
        return forwarding
      } finally {
        setLoading(false)
      }
    },
    [client, fetchApi],
  )
  return { perform, loading }
}

export function useRetryInboundMessage(): MutationResult<InboundMessage, [string]> {
  const { fetchApi } = useFetchApi()
  const client = useQueryClient()
  const [loading, setLoading] = useState(false)
  const perform = useCallback(
    async (id: string) => {
      setLoading(true)
      try {
        const message = await fetchApi<InboundMessage>(`/api/inbound-messages/${encodeURIComponent(id)}/retry`, {
          method: 'POST',
        })
        await cacheRetriedInboundMessage(client, message)
        return message
      } finally {
        setLoading(false)
      }
    },
    [client, fetchApi],
  )
  return { perform, loading }
}
